import { Session } from "@/lib/api/sessions.ts";
import { Fragment, useState } from "react";
import { cn } from "@/lib/utils.ts";
import { Trash } from "lucide-react";
import { Badge } from "@/components/ui/badge.tsx";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu.tsx";
import { useFormatEventDetails } from "@/lib/hooks/use-format-event-details.ts";
import { SessionEditMode } from "@/components/session/session-edit-mode.tsx";

export interface SessionCardProps {
  session: Session;
  readonlyMode?: boolean;
  onDelete: () => void;
}

export function SessionCard({
  session,
  onDelete,
  readonlyMode,
}: SessionCardProps) {
  const [editMode, setEditMode] = useState(false);
  const { day, date, month, startsIn, time } = useFormatEventDetails(
    session.scheduled_time,
  );

  return (
    <ContextMenu>
      <ContextMenuTrigger className="w-full">
        <div
          className={cn(
            "h-auto w-full border-spacing-4 rounded-xl border-2 border-zinc-300 bg-white px-4 py-3 transition-all duration-200 ease-in-out",
            {
              "cursor-pointer": !!session.note,
              "border-indigo-500": editMode,
            },
          )}
          onClick={() => {
            if (editMode || readonlyMode) return;

            setEditMode(!editMode);
          }}
        >
          {editMode ? (
            <SessionEditMode
              session={session}
              closeForm={() => setEditMode(false)}
            />
          ) : (
            <Fragment>
              <div className="relative flex items-center">
                <div className="flex flex-shrink-0 items-center justify-center">
                  <div className="text-center">
                    <p className="text-md -mb-1 font-bold uppercase">{day}</p>
                    <p className="text-2xl font-bold">{date}</p>
                    <p className="text-md -mt-1 font-bold uppercase">{month}</p>
                  </div>
                </div>
                <div className="ml-4 border-l-2 border-black py-2 pl-4">
                  <p className="mb-2 text-base font-normal">{session.title}</p>
                  <p className="text-center text-sm font-normal leading-snug text-stone-500">
                    {startsIn} • {time}
                  </p>
                </div>

                {session.host?.id !== session.mentor?.id && (
                  <Badge className="absolute right-0 top-0">Mentee</Badge>
                )}
              </div>

              {!!session.note && (
                <div className="mt-4">
                  <p className="text-sm font-normal text-stone-500">
                    {session.note}
                  </p>
                </div>
              )}
            </Fragment>
          )}
        </div>
      </ContextMenuTrigger>

      {!readonlyMode && (
        <ContextMenuContent className="p-0">
          <ContextMenuItem
            className="flex cursor-pointer gap-2 border-t px-4 py-2"
            onClick={onDelete}
          >
            <Trash size={16} className="text-red-500" />
            <span className="text-sm font-normal leading-relaxed text-red-500">
              Cancel session
            </span>
          </ContextMenuItem>
        </ContextMenuContent>
      )}
    </ContextMenu>
  );
}
